/**
 * UI enhancements and utility functions
 */

export function initializeUIEnhancements() {
  // Handle delete confirmations
  const deleteButtons = document.querySelectorAll('.delete-confirm');
  deleteButtons.forEach((button) => {
    button.addEventListener('click', (e) => {
      if (!confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
        e.preventDefault();
      }
    });
  });

  // Confirm dialogs for delete actions
  const deleteButtonsWithConfirm = document.querySelectorAll('[data-confirm]');
  deleteButtonsWithConfirm.forEach((button) => {
    button.addEventListener('click', function (e) {
      const message = this.dataset.confirm || 'Are you sure you want to delete this item?';
      if (!confirm(message)) {
        e.preventDefault();
      }
    });
  });

  // Enhanced loading states
  const asyncButtons = document.querySelectorAll('[data-async]');
  asyncButtons.forEach((button) => {
    button.addEventListener('click', function () {
      const originalNodes = Array.from(this.childNodes).map((node) => node.cloneNode(true));
      this.disabled = true;

      const spinner = document.createElement('i');
      spinner.setAttribute('data-lucide', 'loader-2');
      spinner.className = 'lucide-spin me-1';
      this.replaceChildren(spinner, document.createTextNode(' Loading...'));
      if (window.lucide) window.lucide.createIcons();

      // Re-enable after 5 seconds as fallback
      setTimeout(() => {
        this.disabled = false;
        this.replaceChildren(...originalNodes);
      }, 5000);
    });
  });

  // Copy-to-clipboard buttons. `data-copy` is a selector for the element holding the
  // text; `data-copy-icon` names the lucide icon shown on success (defaults to "check").
  const copyButtons = document.querySelectorAll('[data-copy]');
  copyButtons.forEach((button) => {
    const originalNodes = Array.from(button.childNodes).map((node) => node.cloneNode(true));
    const successIcon = button.dataset.copyIcon || 'check';

    button.addEventListener('click', function () {
      const target = document.querySelector(this.dataset.copy);
      if (!target) return;

      if (typeof target.select === 'function') {
        target.select();
        target.setSelectionRange?.(0, 99999);
      }

      const text = target.value !== undefined ? target.value : target.textContent;
      navigator.clipboard.writeText(text).then(() => {
        const icon = document.createElement('i');
        icon.setAttribute('data-lucide', successIcon);
        this.replaceChildren(icon, document.createTextNode(' Copied!'));
        this.classList.remove('btn-outline-secondary');
        this.classList.add('btn-success');
        if (window.lucide) window.lucide.createIcons();

        setTimeout(() => {
          this.replaceChildren(...originalNodes.map((node) => node.cloneNode(true)));
          this.classList.remove('btn-success');
          this.classList.add('btn-outline-secondary');
          if (window.lucide) window.lucide.createIcons();
        }, 2000);
      });
    });
  });

  // Print functionality
  const printButtons = document.querySelectorAll('[data-print]');
  printButtons.forEach((button) => {
    button.addEventListener('click', function () {
      const target = document.querySelector(this.dataset.print);
      if (target) {
        const printWindow = window.open('', '_blank');
        // eslint-disable-next-line no-unsanitized/method
        printWindow.document.write(
          '<html><head><title>Print</title><link rel="stylesheet" href="/static/css/style.css"><style>@media print { .no-print { display: none !important; } body { margin: 0; padding: 20px; } }</style></head><body>' +
            target.innerHTML +
            '</body></html>'
        );
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    });
  });

  // Lazy loading for images
  const images = document.querySelectorAll('img[data-src]');
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        observer.unobserve(img);
      }
    });
  });

  images.forEach((img) => imageObserver.observe(img));
}

// Theme persistence
export function initializeTheme() {
  // Initialize theme from localStorage first, then sync with server if user is logged in
  initializeThemeFromStorage();

  // Sync with server theme if user is authenticated
  if (window.userAuthenticated) {
    syncThemeWithServer();
  }

  // Set up theme toggle functionality
  setupThemeToggle();
}

function initializeThemeFromStorage() {
  // Get theme from localStorage or fall back to server-provided theme
  const localTheme = localStorage.getItem('theme');
  const serverTheme = window.currentTheme || 'light';
  const currentTheme = localTheme || serverTheme;

  // Apply theme immediately
  document.documentElement.setAttribute('data-theme', currentTheme);

  // Sync checkbox state with current theme
  const themeCheckbox = document.querySelector('#themeToggle');
  if (themeCheckbox) {
    themeCheckbox.checked = currentTheme === 'dark';
  }

  // Store in localStorage if not already present
  if (!localTheme) {
    localStorage.setItem('theme', currentTheme);
  }
}

async function syncThemeWithServer() {
  try {
    // Get user's theme preference from server
    const response = await fetch('/api/user/theme');
    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        const serverTheme = data.theme;
        const localTheme = localStorage.getItem('theme');

        // If themes differ, prioritize localStorage (user's most recent choice)
        if (localTheme && localTheme !== serverTheme) {
          // Update server with localStorage theme
          await updateServerTheme(localTheme);
        } else if (!localTheme) {
          // No local theme, use server theme
          localStorage.setItem('theme', serverTheme);
          document.documentElement.setAttribute('data-theme', serverTheme);
        }
      }
    }
  } catch (error) {
    console.warn('Failed to sync theme with server:', error);
  }
}

async function updateServerTheme(theme) {
  try {
    const response = await fetch('/api/user/theme', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ theme: theme }),
    });

    if (!response.ok) {
      console.warn('Failed to update server theme preference');
    }
  } catch (error) {
    console.warn('Failed to update server theme:', error);
  }
}

function setupThemeToggle() {
  const themeToggle = document.querySelector('#themeToggle');
  if (themeToggle) {
    themeToggle.addEventListener('change', async function () {
      const newTheme = this.checked ? 'dark' : 'light';

      // Apply theme immediately
      document.documentElement.setAttribute('data-theme', newTheme);
      localStorage.setItem('theme', newTheme);

      // Update server if user is authenticated
      if (window.userAuthenticated) {
        await updateServerTheme(newTheme);
      }
    });
  }
}

// Export theme toggle function for external use
export function toggleTheme() {
  const currentTheme = document.documentElement.getAttribute('data-theme');
  const newTheme = currentTheme === 'dark' ? 'light' : 'dark';

  // Apply theme immediately
  document.documentElement.setAttribute('data-theme', newTheme);
  localStorage.setItem('theme', newTheme);

  // Update server if user is authenticated
  if (window.userAuthenticated) {
    updateServerTheme(newTheme);
  }

  return newTheme;
}
